import { useState, useCallback, useEffect } from 'react';
import { useWallet } from '../../../context/WalletContext';
import {
  fetchStakingDashboard,
  fetchStakeDetail,
  executeStake,
  executeUnstakeRequest,
  executeClaim,
  executeClaimAll,
  executeCancelUnstakeRequest,
} from './stakingContractBridge';
import { MOCK_STAKING_PORTFOLIO } from './stakingMockData';

/**
 * Estado UI de Staking. Datos y transacciones pasan por `stakingContractBridge.js`
 * (mock si no hay contrato; Polygon cuando implementes las funciones marcadas IMPLEMENTAR).
 */
export function useStakingSkeleton() {
  const { signer, provider } = useWallet();
  const reader = signer || provider;

  const [activeTab, setActiveTab] = useState('stakes');
  const [overlay, setOverlay] = useState(null);
  /** null | 'processing' | 'stake_pending' | 'unstake_processing' */

  const [portfolio, setPortfolio] = useState(null);
  const [activeStakes, setActiveStakes] = useState([]);
  const [unstakeSummary, setUnstakeSummary] = useState(null);
  const [unstakeItems, setUnstakeItems] = useState([]);
  const [dashboardError, setDashboardError] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  const [stakeModalOpen, setStakeModalOpen] = useState(false);
  const [stakeDetailsModal, setStakeDetailsModal] = useState(null);
  const [unstakeRequestModal, setUnstakeRequestModal] = useState(null);
  const [unstakeClaimModal, setUnstakeClaimModal] = useState(null);
  const [resultModal, setResultModal] = useState(null);
  /** { variant: 'stake_success'|'stake_error'|'unstake_request_success'|'unstake_request_error', detail?: string } */

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const data = await fetchStakingDashboard(reader);
      setPortfolio(data.portfolio);
      setActiveStakes(data.activeStakes);
      setUnstakeSummary(data.unstakeSummary);
      setUnstakeItems(data.unstakeItems);
    } catch (e) {
      setDashboardError(e?.message || 'LOAD_FAILED');
    } finally {
      setDashboardLoading(false);
    }
  }, [reader]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const openStakeModal = useCallback(() => setStakeModalOpen(true), []);
  const closeStakeModal = useCallback(() => setStakeModalOpen(false), []);

  const openStakeDetails = useCallback(
    async (stakeId) => {
      try {
        const detail = await fetchStakeDetail(stakeId, reader);
        setStakeDetailsModal(detail);
      } catch (e) {
        setResultModal({
          variant: 'stake_error',
          detail: e?.message || 'DETAIL_FAILED',
        });
      }
    },
    [reader]
  );
  const closeStakeDetails = useCallback(() => setStakeDetailsModal(null), []);

  const openUnstakeRequest = useCallback((item) => {
    const sym = portfolio?.stakeTokenSymbol || MOCK_STAKING_PORTFOLIO.stakeTokenSymbol;
    setUnstakeRequestModal({
      amount: item?.amount ?? '500.00',
      fee: item?.penaltyFee ?? '50.00',
      totalClaim: item?.totalClaim ?? '450.00',
      tokenSymbol: sym,
      raw: item,
    });
  }, [portfolio?.stakeTokenSymbol]);
  const closeUnstakeRequest = useCallback(() => setUnstakeRequestModal(null), []);

  const openUnstakeClaim = useCallback((item) => {
    const sym = portfolio?.stakeTokenSymbol || MOCK_STAKING_PORTFOLIO.stakeTokenSymbol;
    setUnstakeClaimModal({
      amount: item?.amount ?? '500.00',
      fee: item?.penaltyFee ?? '50.00',
      totalClaim: item?.totalClaim ?? '450.00',
      tokenSymbol: sym,
      raw: item,
    });
  }, [portfolio?.stakeTokenSymbol]);
  const closeUnstakeClaim = useCallback(() => setUnstakeClaimModal(null), []);

  const closeResultModal = useCallback(() => setResultModal(null), []);

  const submitStake = useCallback(
    async (amount) => {
      setOverlay('processing');
      const res = await executeStake(signer, String(amount ?? ''));
      setOverlay(null);
      if (!res.ok) {
        setStakeModalOpen(false);
        setResultModal({
          variant: 'stake_error',
          detail: res.error === 'WALLET_REQUIRED' ? 'WALLET_REQUIRED' : res.error,
        });
        return;
      }
      setStakeModalOpen(false);
      setOverlay('stake_pending');
      await new Promise((r) => setTimeout(r, 900));
      setOverlay(null);
      setResultModal({ variant: 'stake_success' });
      loadDashboard();
    },
    [signer, loadDashboard]
  );

  const submitUnstakeRequest = useCallback(async () => {
    const payload = unstakeRequestModal;
    setOverlay('unstake_processing');
    const res = await executeUnstakeRequest(signer, payload);
    setOverlay(null);
    setUnstakeRequestModal(null);
    if (!res.ok) {
      setResultModal({
        variant: 'unstake_request_error',
        detail: res.error === 'WALLET_REQUIRED' ? 'WALLET_REQUIRED' : res.error,
      });
      return;
    }
    setResultModal({ variant: 'unstake_request_success' });
    loadDashboard();
  }, [signer, unstakeRequestModal, loadDashboard]);

  const submitClaim = useCallback(async () => {
    const payload = unstakeClaimModal;
    setOverlay('unstake_processing');
    const res = await executeClaim(signer, payload);
    setOverlay(null);
    setUnstakeClaimModal(null);
    if (!res.ok) {
      setResultModal({
        variant: 'unstake_request_error',
        detail: res.error === 'WALLET_REQUIRED' ? 'WALLET_REQUIRED' : res.error,
      });
      return;
    }
    setResultModal({ variant: 'unstake_request_success' });
    loadDashboard();
  }, [signer, unstakeClaimModal, loadDashboard]);

  const submitClaimAll = useCallback(async () => {
    setOverlay('unstake_processing');
    const res = await executeClaimAll(signer);
    setOverlay(null);
    if (!res.ok) {
      setResultModal({
        variant: 'unstake_request_error',
        detail: res.error === 'WALLET_REQUIRED' ? 'WALLET_REQUIRED' : res.error,
      });
      return;
    }
    setResultModal({ variant: 'unstake_request_success' });
    loadDashboard();
  }, [signer, loadDashboard]);

  const cancelUnstakeRequest = useCallback(
    async (requestId) => {
      setOverlay('processing');
      const res = await executeCancelUnstakeRequest(signer, requestId);
      setOverlay(null);
      if (!res.ok) {
        setResultModal({
          variant: 'unstake_request_error',
          detail: res.error === 'WALLET_REQUIRED' ? 'WALLET_REQUIRED' : res.error,
        });
        return;
      }
      loadDashboard();
    },
    [signer, loadDashboard]
  );

  const dismissOverlay = useCallback(() => setOverlay(null), []);

  return {
    activeTab,
    setActiveTab,
    overlay,
    dismissOverlay,
    portfolio,
    activeStakes,
    unstakeSummary,
    unstakeItems,
    dashboardLoading,
    dashboardError,
    reloadDashboard: loadDashboard,
    resultModal,
    closeResultModal,
    modals: {
      stakeOpen: stakeModalOpen,
      openStake: openStakeModal,
      closeStake: closeStakeModal,
      stakeDetails: stakeDetailsModal,
      openStakeDetails,
      closeStakeDetails,
      unstakeRequest: unstakeRequestModal,
      openUnstakeRequest,
      closeUnstakeRequest,
      unstakeClaim: unstakeClaimModal,
      openUnstakeClaim,
      closeUnstakeClaim,
    },
    actions: {
      submitStake,
      submitUnstakeRequest,
      submitClaim,
      submitClaimAll,
      cancelUnstakeRequest,
    },
  };
}
